<template>
    <div v-if="loading" class="tl-page">
        <div class="tl-loading">
            <div class="tl-spinner"></div>
            <span>Loading tour statistics...</span>
        </div>
    </div>

    <div v-else class="tl-page">
        <div class="tl-header-bar">
            <h1 class="tl-title">{{ tourName }} - Statistics</h1>
            <div class="tl-header-actions">
                <router-link :to="{ path: '/calculation-london/tourstats' }">
                    <button class="tl-btn tl-btn--ghost">← Back to Groups</button>
                </router-link>
            </div>
        </div>

        <!-- Side-by-Side Comparison Section -->
        <div class="tl-stats-grid mb-4">
            <!-- Selling Price Pies -->
            <div class="tl-info-card">
                <div class="tl-section-header">
                    <h2 class="tl-section-title">Selling Price (Adult)</h2>
                    <p class="tl-section-desc">Price distribution per tier across years</p>
                </div>
                <div class="pie-grid-container mt-4">
                    <div v-for="tierValue in tiers" :key="'sell_' + tierValue" class="pie-card">
                        <div class="pie-chart-wrapper">
                            <canvas :id="'sellChart_' + tierValue"></canvas>
                        </div>
                        <div class="pie-meta">
                            <span class="tl-pill tl-pill--orange">Tier {{ tierValue }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- YoY Growth Bar Chart -->
            <div class="tl-info-card">
                <div class="tl-section-header">
                    <h2 class="tl-section-title">Annual Price Variance (%)</h2>
                    <p class="tl-section-desc">Year-over-year price change per tier</p>
                </div>
                <div class="chart-container mt-4">
                    <canvas id="growthBarChart"></canvas>
                    <div v-if="years.length < 2" class="pie-empty">
                        <span>New Tour (No growth history yet)</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="tl-info-card mb-4">
            <div class="tl-section-header">
                <h2 class="tl-section-title">Overall Price Evolution (%)</h2>
                <p class="tl-section-desc">Cumulative price change from baseline year</p>
            </div>
            <div class="chart-container">
                <canvas id="overallGrowthChart"></canvas>
                <div v-if="years.length < 2" class="pie-empty">
                    <span>New Tour (No history to track performance)</span>
                </div>
            </div>
        </div>

        <!-- Item Price Comparison -->
        <div class="tl-info-card">
            <div class="tl-section-header">
                <h2 class="tl-section-title">Item Price Comparison (Net Adult)</h2>
                <p class="tl-section-desc">Tracking component cost history</p>
            </div>
            <div class="chart-container">
                <canvas id="itemChart"></canvas>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';

export default {
    name: 'TourstatsLondon',
    data() {
        return {
            loading: true,
            tourName: '',
            years: [],
            tiers: [],
            sellingData: {}, // Tier -> [Prices for each year]
            itemData: {},    // ItemName -> [Prices for each year]
            percentageData: [],
            overallGrowthData: [], // Tier -> [Cumulative % for each year]
            chartInstances: [],
            renderTimeout: null,
            isComponentMounted: false
        };
    },
    mounted() {
        this.isComponentMounted = true;
        this.fetchTourData();
    },
    beforeUnmount() {
        this.isComponentMounted = false;
        if (this.renderTimeout) clearTimeout(this.renderTimeout);
        this.destroyCharts();
    },
    methods: {
        async fetchTourData() {
            // Destroy charts BEFORE setting loading=true to ensure canvases are still in DOM
            this.destroyCharts();
            if (this.renderTimeout) clearTimeout(this.renderTimeout);

            this.loading = true;
            try {
                const tourCode = this.$route.params.tourCode;
                console.log('[Tourstats] Loading for code:', tourCode);

                // Fixed population and removed field filters to ensure tiers are loaded
                const response = await this.$xhr.get(`/api/tourlondons?filters[tourCode][$eq]=${tourCode}&populate[tiers]=*&populate[item]=*&pagination[limit]=100`);

                const tours = response.data.data;
                if (tours && tours.length > 0) {
                    this.tourName = tours[0].attributes.tourName;
                    this.years = [...new Set(tours.map(t => t.attributes.year))].filter(Boolean).sort((a, b) => a - b);
                    this.tiers = [...new Set(tours.flatMap(t => (t.attributes.tiers || []).map(tr => tr.value)))].filter(v => v != null).sort((a, b) => a - b);

                    this.prepareSellingData(tours);
                    this.prepareItemData(tours);
                    this.calculatePercentageChange();

                    this.loading = false;

                    // Delay ensures the DOM is fully painted and canvas has dimensions
                    this.renderTimeout = setTimeout(() => {
                        if (!this.isComponentMounted || this.loading) return;

                        console.log('[Tourstats] Initializing charts...');
                        this.createSellingPies();
                        this.createItemChart();
                        this.createGrowthBarChart();
                        this.createOverallGrowthChart();
                    }, 250);
                } else {
                    console.warn('[Tourstats] No data found');
                    this.loading = false;
                }
            } catch (error) {
                console.error('[Tourstats] Fetch error:', error);
                this.loading = false;
            }
        },

        prepareSellingData(tours) {
            this.sellingData = {};
            this.tiers.forEach(tierValue => {
                this.sellingData[tierValue] = this.years.map(year => {
                    const tourForYear = tours.find(t => t.attributes.year === year);
                    if (tourForYear && tourForYear.attributes.tiers) {
                        const tier = tourForYear.attributes.tiers.find(tr => tr.value === tierValue);
                        return tier?.userInputSellingPriceAdult || 0;
                    }
                    return 0;
                });
            });
            console.log('[Tourstats] Selling Data Prepared:', this.sellingData);
        },

        prepareItemData(tours) {
            this.itemData = {};
            const allItems = tours.flatMap(t => (t.attributes.item?.data || []));
            const allItemNames = [...new Set(allItems.map(i => i.attributes.item))].filter(Boolean);

            allItemNames.forEach(itemName => {
                this.itemData[itemName] = this.years.map(year => {
                    const tourForYear = tours.find(t => t.attributes.year === year);
                    if (tourForYear && tourForYear.attributes.item?.data) {
                        const item = tourForYear.attributes.item.data.find(i => i.attributes.item === itemName);
                        return item?.attributes?.adultPrice_net || 0;
                    }
                    return 0;
                });
            });
            console.log('[Tourstats] Item Data Prepared:', this.itemData);
        },

        calculatePercentageChange() {
            this.percentageData = [];
            this.overallGrowthData = [];
            this.tiers.forEach((tierValue) => {
                const prices = this.sellingData[tierValue] || [];

                // YoY Changes (Year-to-Year)
                let yoyChanges = [];
                for (let i = 0; i < prices.length - 1; i++) {
                    const prev = prices[i];
                    const curr = prices[i + 1];
                    yoyChanges.push(prev > 0 ? ((curr - prev) / prev) * 100 : 0);
                }
                this.percentageData.push(yoyChanges);

                // Cumulative Growth (From First Year Price)
                let cumulative = [];
                if (prices.length > 0) {
                    const baseline = prices[0];
                    prices.forEach(curr => {
                        cumulative.push(baseline > 0 ? ((curr - baseline) / baseline) * 100 : 0);
                    });
                }
                this.overallGrowthData.push(cumulative);
            });
        },

        destroyCharts() {
            console.log('[Tourstats] Destroying existing charts:', this.chartInstances.length);
            if (this.chartInstances && this.chartInstances.length > 0) {
                this.chartInstances.forEach(chart => {
                    try {
                        if (chart) chart.destroy();
                    } catch (e) {
                        console.warn('[Tourstats] Error destroying chart:', e);
                    }
                });
                this.chartInstances = [];
            }
        },

        createSellingPies() {
            this.tiers.forEach((tierValue) => {
                const el = document.getElementById('sellChart_' + tierValue);
                if (!el) {
                    console.warn(`[Tourstats] Canvas sellChart_${tierValue} not found`);
                    return;
                }

                const chart = new Chart(el, {
                    type: 'pie',
                    data: {
                        labels: this.years.map(y => `Year ${y}`),
                        datasets: [{
                            data: this.sellingData[tierValue],
                            backgroundColor: ['#3B82F6', '#E2923D', '#10B981', '#F43F5E', '#8B5CF6'],
                            borderWidth: 2,
                            borderColor: '#fff'
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } },
                            tooltip: { callbacks: { label: (ctx) => `${ctx.label}: €${ctx.raw}` } }
                        }
                    }
                });
                this.chartInstances.push(chart);
            });
        },

        createItemChart() {
            const el = document.getElementById('itemChart');
            if (!el) return;

            const itemNames = Object.keys(this.itemData);
            if (itemNames.length === 0) return;

            const chart = new Chart(el, {
                type: 'line',
                data: {
                    labels: this.years,
                    datasets: itemNames.map((name, index) => ({
                        label: name,
                        data: this.itemData[name],
                        borderColor: `hsl(${index * 137.5 % 360}, 70%, 50%)`,
                        backgroundColor: `hsla(${index * 137.5 % 360}, 70%, 50%, 0.1)`,
                        tension: 0.3,
                        pointRadius: 4,
                        fill: false
                    }))
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, padding: 15 } } },
                    scales: {
                        y: { beginAtZero: true, title: { display: true, text: 'Net Price (€)' } },
                        x: { title: { display: true, text: 'Year' } }
                    }
                }
            });
            this.chartInstances.push(chart);
        },

        createGrowthBarChart() {
            const el = document.getElementById('growthBarChart');
            if (!el || this.years.length < 2) return;

            const chart = new Chart(el, {
                type: 'bar',
                data: {
                    labels: this.years.slice(1).map((y, i) => `${this.years[i]} → ${y}`),
                    datasets: this.tiers.map((tierValue, index) => ({
                        label: `Tier ${tierValue}`,
                        data: this.percentageData[index],
                        backgroundColor: `hsla(${index * 60 % 360}, 70%, 60%, 0.8)`,
                        borderRadius: 6
                    }))
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { position: 'bottom', labels: { boxWidth: 12 } },
                        tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toFixed(2)}%` } }
                    },
                    scales: {
                        y: {
                            title: { display: true, text: 'Price Variance (%)' },
                            grid: { color: '#f1f5f9' },
                            ticks: { callback: (val) => val + '%' }
                        },
                        x: { grid: { display: false } }
                    }
                }
            });
            this.chartInstances.push(chart);
        },

        createOverallGrowthChart() {
            const el = document.getElementById('overallGrowthChart');
            if (!el || this.years.length < 2) return;

            const chart = new Chart(el, {
                type: 'line',
                data: {
                    labels: this.years,
                    datasets: this.tiers.map((tierValue, index) => ({
                        label: `Tier ${tierValue}`,
                        data: this.overallGrowthData[index],
                        borderColor: `hsl(${index * 60 % 360}, 75%, 60%)`,
                        backgroundColor: `hsla(${index * 60 % 360}, 75%, 60%, 0.1)`,
                        tension: 0.3,
                        pointRadius: 5,
                        fill: true
                    }))
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { position: 'bottom' },
                        tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toFixed(2)}% total change` } }
                    },
                    scales: {
                        y: {
                            title: { display: true, text: 'Cumulative Evolution (%)' },
                            ticks: { callback: (val) => val + '%' }
                        }
                    }
                }
            });
            this.chartInstances.push(chart);
        }
    }
};
</script>

<style scoped>
.tl-page {
    padding: 16px 20px;
    font-family: 'Inter', sans-serif;
    background: #f8fafc;
    min-height: 100vh;
}

.tl-header-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fff;
    padding: 12px 20px;
    border-radius: 10px;
    border: 1px solid #e9ecef;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.tl-title {
    font-size: 1.5rem;
    font-weight: 800;
    color: #111827;
    margin: 0;
}

.tl-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 50vh;
    gap: 12px;
}

.tl-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e2e8f0;
    border-top-color: #E2923D;
    border-radius: 50%;
    animation: tl-spin .8s linear infinite;
}

@keyframes tl-spin {
    to {
        transform: rotate(360deg);
    }
}

.tl-stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    align-items: start;
}

@media (max-width: 1024px) {
    .tl-stats-grid {
        grid-template-columns: 1fr;
    }
}

.tl-info-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    height: 100%;
    display: flex;
    flex-direction: column;
}

.tl-section-header {
    margin-bottom: 20px;
    border-bottom: 1px solid #f1f5f9;
    padding-bottom: 12px;
}

.tl-section-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #334155;
    margin: 0;
}

.tl-section-desc {
    font-size: 0.85rem;
    color: #64748b;
    margin: 4px 0 0;
}

.chart-container {
    position: relative;
    height: 350px;
    width: 100%;
    flex-grow: 1;
}

.pie-grid-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
}

.pie-card {
    background: #fdfdfd;
    border: 1px solid #f8fafc;
    border-radius: 10px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.pie-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.pie-chart-wrapper {
    position: relative;
    height: 140px;
    width: 100%;
}

.pie-meta {
    margin-top: 8px;
    width: 100%;
    text-align: center;
}

.pie-empty {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 0.9rem;
    font-style: italic;
    text-align: center;
}

.tl-pill {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 5px;
    font-size: .7rem;
    font-weight: 700;
    color: #fff;
}

.tl-pill--orange {
    background: #E2923D;
}

.tl-btn {
    height: 32px;
    padding: 0 14px;
    font-size: .8rem;
    font-weight: 600;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #fff;
    color: #475569;
    cursor: pointer;
    transition: all 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.tl-btn--ghost:hover {
    background: #f8fafc;
    border-color: #E2923D;
    color: #E2923D;
}

.mb-4 {
    margin-bottom: 2rem;
}

.mt-4 {
    margin-top: 1rem;
}
</style>